import { ref, computed, watch } from "vue";

/**
 * 멀티 문항(questions[]) 공용 컴포저블.
 * Practice 컴포넌트가 한 섹션에 여러 문항을 등록/삭제/전환할 수 있게 한다.
 *
 * @param {import('vue').Ref} sectionData  defineModel 로 받은 sectionData ref
 * @param {Function} makeEmpty             () => 새 빈 문항 객체(매 호출 새 배열/객체 반환)
 * @param {string[]} legacyKeys            기존 단일(평면) 콘텐츠를 questions[0]로 옮길 때 이동할 키들
 *
 * @returns { questions, currentIndex, current, selectQuestion, addQuestion, removeQuestion }
 */
export function useQuestions(sectionData, makeEmpty, legacyKeys = []) {
  const currentIndex = ref(0);

  // 구조 초기화 + 레거시(단일 평면 콘텐츠) 마이그레이션 + 인덱스 보정
  watch(
    () => sectionData.value.content,
    (content) => {
      if (!content) return;
      // 직전 problems[] 단계에서 저장된 데이터 보호
      if (!Array.isArray(content.questions) && Array.isArray(content.problems)) {
        content.questions = content.problems;
        delete content.problems;
      }
      if (!Array.isArray(content.questions)) {
        const hasLegacy = legacyKeys.some((k) => content[k] !== undefined);
        if (hasLegacy) {
          const q = makeEmpty();
          legacyKeys.forEach((k) => {
            if (content[k] !== undefined) {
              q[k] = content[k];
              delete content[k];
            }
          });
          content.questions = [q];
        } else {
          content.questions = [makeEmpty()];
        }
      }
      if (currentIndex.value > content.questions.length - 1) {
        currentIndex.value = Math.max(0, content.questions.length - 1);
      }
    },
    { immediate: true, deep: true }
  );

  const questions = computed(() => sectionData.value.content.questions || []);

  const current = computed(() => {
    const qs = questions.value;
    if (!qs.length) return null;
    return qs[Math.min(currentIndex.value, qs.length - 1)];
  });

  const selectQuestion = (index) => {
    currentIndex.value = index;
  };

  const addQuestion = () => {
    sectionData.value.content.questions.push(makeEmpty());
    currentIndex.value = sectionData.value.content.questions.length - 1;
  };

  const removeQuestion = (index) => {
    const qs = sectionData.value.content.questions;
    if (qs.length <= 1) return;
    qs.splice(index, 1);
    if (currentIndex.value >= qs.length) {
      currentIndex.value = qs.length - 1;
    } else if (currentIndex.value > index) {
      currentIndex.value--;
    }
  };

  return { questions, currentIndex, current, selectQuestion, addQuestion, removeQuestion };
}
